<script lang="ts">
  import { navigationIntent, type NavigationIntent } from '../actions/navigation-intent';
  import type { SearchResult } from '../reader/types';

  const ROW_HEIGHT = 108;
  const OVERSCAN = 4;

  let {
    results,
    onSelect,
  }: {
    results: SearchResult[];
    onSelect: (result: SearchResult, intent: NavigationIntent) => void;
  } = $props();

  let scroller = $state.raw<HTMLDivElement | null>(null);
  let scrollTop = $state(0);
  let viewportHeight = $state(0);

  const totalHeight = $derived(results.length * ROW_HEIGHT);
  const startIndex = $derived(Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN));
  const effectiveViewportHeight = $derived(viewportHeight || ROW_HEIGHT * 6);
  const visibleCount = $derived(Math.ceil(effectiveViewportHeight / ROW_HEIGHT) + OVERSCAN * 2);
  const endIndex = $derived(Math.min(results.length, startIndex + visibleCount));
  const offsetY = $derived(startIndex * ROW_HEIGHT);
  const visibleResults = $derived(results.slice(startIndex, endIndex));

  $effect(() => {
    results;
    scrollTop = 0;
    if (scroller) {
      scroller.scrollTop = 0;
    }
  });

  function handleScroll(event: Event): void {
    scrollTop = (event.currentTarget as HTMLDivElement).scrollTop;
  }
</script>

<div
  bind:this={scroller}
  bind:clientHeight={viewportHeight}
  class="search-results-list"
  data-cy="search-results"
  aria-label="Search results"
  onscroll={handleScroll}
>
  <div class="search-results-list__spacer" style:height={`${totalHeight}px`}>
    <div class="search-results-list__window" style:transform={`translateY(${offsetY}px)`}>
      {#each visibleResults as result (`${result.bookId}:${result.chapter}:${result.verse}`)}
        <button
          type="button"
          class="search-results-list__result"
          data-cy="search-result"
          data-result-ref={`${result.bookName} ${result.chapter}:${result.verse}`}
          style:height={`${ROW_HEIGHT}px`}
          use:navigationIntent={{
            onIntent: ({ intent }) => onSelect(result, intent),
          }}
        >
          <span class="search-results-list__result-ref">
            {result.bookName} {result.chapter}:{result.verse}
          </span>
          <span class="search-results-list__result-text">{result.text}</span>
        </button>
      {/each}
    </div>
  </div>
</div>

<style>
  .search-results-list {
    box-sizing: border-box;
    width: 100%;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
    border: 1px solid rgba(104, 74, 44, 0.12);
    border-radius: 1.25rem;
    background: linear-gradient(180deg, #fffaf3 0%, #f5ebdc 100%);
    box-shadow: 0 1rem 2.2rem rgba(45, 28, 11, 0.2);
  }

  .search-results-list__spacer {
    position: relative;
    width: 100%;
  }

  .search-results-list__window {
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    display: grid;
    padding: 0.45rem;
    box-sizing: border-box;
  }

  .search-results-list__result {
    box-sizing: border-box;
    display: grid;
    align-content: start;
    gap: 0.25rem;
    width: 100%;
    min-width: 0;
    padding: 0.72rem 0.8rem;
    border: 0;
    border-radius: 0.95rem;
    background: transparent;
    color: #2a1d12;
    text-align: left;
    cursor: pointer;
  }

  .search-results-list__result:hover,
  .search-results-list__result:focus-visible {
    background: rgba(104, 74, 44, 0.08);
    outline: none;
  }

  :global(.search-results-list__result.navigation-intent-target--arming) {
    background: rgba(224, 238, 255, 0.96);
    box-shadow:
      inset 0 0 0 1px rgba(52, 91, 150, 0.18),
      0 0.45rem 0.9rem rgba(45, 82, 141, 0.12);
    transform: scale(0.985);
  }

  .search-results-list__result-ref {
    overflow: hidden;
    color: #855f27;
    font-family: 'Avenir Next Condensed', 'Arial Narrow', Arial, sans-serif;
    font-size: 0.9rem;
    font-weight: 700;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .search-results-list__result-text {
    display: -webkit-box;
    overflow: hidden;
    color: #3b2818;
    font-size: 0.92rem;
    line-height: 1.45;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
  }
</style>
